import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Accessibility, MessageCircle, Phone, PawPrint, Eye, Users, Tag, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { RoomCard } from "@/components/rooms/RoomCard";
import { Reveal, Img, Container, SectionTitle, HScroll, Chip, PhotoHero } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent, minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";
import { HOTEL } from "@/lib/hotel";
import { FACILITY_META } from "@/lib/facilities";

/* Backwards-compatible export (older imports) */
export { FACILITIES } from "@/lib/facilities";

const FILTERS = [
  { id: "all", icon: null, nn: "Alle rom", en: "All rooms", test: () => true },
  { id: "sea_view", icon: Eye, nn: "Fjordutsikt", en: "Fjord view", test: (r) => (r.badges || []).includes("sea_view") },
  { id: "dog", icon: PawPrint, nn: "Hund velkomen", en: "Dogs welcome", test: (r) => r.dogs_allowed },
  { id: "family", icon: Users, nn: "Plass til fleire", en: "Room for more", test: (r) => r.max_guests >= 3 },
  { id: "step_free", icon: Accessibility, nn: "Utan trapper", en: "Step-free", test: (r) => r.step_free },
  { id: "best_value", icon: Tag, nn: "Best pris", en: "Best value", test: (r) => (r.badges || []).includes("best_value") },
];

const COMMON_AREAS = [
  { src: "/images/lounge.jpg", nn: "Peisestova", en: "The fireplace lounge", aspect: "aspect-square" },
  { src: "/images/restaurant-window.jpg", nn: "Restauranten – bord ved vindauget", en: "The restaurant – a table by the window", aspect: "aspect-[4/5]" },
  { src: "/images/restaurant-hall.jpg", nn: "Restauranten dekka til fest", en: "The restaurant set for a celebration", aspect: "aspect-[4/3]", w: "w-[84vw] sm:w-[56vw] lg:w-[520px]" },
  { src: "/images/garden-tables.jpg", nn: "Hagen mot sjøen", en: "The garden towards the sea", aspect: "aspect-[4/5]" },
  { src: "/images/pool-wide.jpg", nn: "Sjøkanten Trivsel – bassenget", en: "Sjøkanten Trivsel – the pool", aspect: "aspect-[4/3]", w: "w-[84vw] sm:w-[56vw] lg:w-[520px]", position: "50% 55%" },
  { src: "/images/interior-birch.jpg", nn: "Detalj frå suitene", en: "Detail from the suites", aspect: "aspect-[4/5]" },
];

export default function Rooms() {
  const { lang, tx, tr } = useLang();
  const { data, isLoading } = useContent();
  const { ask } = useConcierge();
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState("all");
  const roomTypes = useMemo(() => (data?.room_types || []).filter((r) => r.active !== false).sort((a, b) => (a.sort || 0) - (b.sort || 0)), [data]);
  const s = data?.settings;
  usePageMeta(
    tx("Rom", "Rooms"),
    tx("51 rom ved kaien i Skånevik – standard, superior med balkong, familierom, fjordsuite og einerom. Frukost inkludert, betal på hotellet.", "51 rooms on the quay in Skånevik – standard, superior with balcony, family rooms, fjord suite and single rooms. Breakfast included, pay at the hotel.")
  );

  const active = FILTERS.find((f) => f.id === filter) || FILTERS[0];
  const shown = roomTypes.filter(active.test);
  const from = roomTypes.length ? Math.min(...roomTypes.map(minPrice)) : null;
  const totalRooms = s?.total_rooms || 51;
  const totalBeds = s?.total_beds || 115;

  const facilityKeys = ["shower", "wifi", "tv", "desk", "coffee", "kettle", "hairdryer", "breakfast", "parking", "pool"];
  const common = COMMON_AREAS.map((c) => ({ ...c, caption: tx(c.nn, c.en), meta: tx("Fellesområde", "Common area") }));

  return (
    <div data-testid="rooms-page">
      <PhotoHero
        testid="rooms-hero"
        image="/images/hotel-aerial.jpg"
        mobileImage="/images/hotel-aerial-portrait.jpg"
        position="50% 34%"
        mobilePosition="50% 30%"
        alt={tx("Skånevik Fjordhotel sett frå lufta", "Skånevik Fjordhotel from the air")}
        kicker={tx(`${totalRooms} rom · ${totalBeds} sengeplassar · alt i same hus`, `${totalRooms} rooms · ${totalBeds} beds · all under one roof`)}
        title={tx("Rom ved fjorden", "Rooms by the fjord")}
        lead={tx("Fem romtypar – frå einerom til fjordsuite. Frukost er inkludert, bassenget er gratis for gjester, og du betaler først når du er her.", "Five room types – from single rooms to the fjord suite. Breakfast is included, the pool is free for guests, and you pay when you arrive.")}
        caption={tx("Hagen og bassenget ligg mellom hotellet og sjøen. Kaien er 100 meter unna.", "The garden and pool lie between the hotel and the sea. The quay is 100 metres away.")}
        height="h-[72svh] min-h-[520px] max-h-[820px]"
      >
        {!isMobile && (
          <div className="mt-9 w-full max-w-[980px]" data-sticky-anchor>
            <BookingBar testid="rooms-booking-bar" />
          </div>
        )}
      </PhotoHero>

      {/* Filters + grid */}
      <section className="pt-10 lg:pt-16" data-testid="rooms-list">
        <Container>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="text-[1.75rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.125rem]">{tx("Vel rommet som passar", "Choose the room that suits you")}</h2>
              <p className="mt-2 text-base text-muted-foreground sm:text-lg tabular" data-testid="rooms-count">
                {roomTypes.length} {tx("romtypar", "room types")}{from && <> · {tx("frå", "from")} <span className="font-semibold text-foreground">{nok(from, lang)}</span> {tx("per natt", "per night")}</>}
              </p>
            </div>
          </div>
          <div className="no-scrollbar -mx-5 mt-6 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="rooms-filters" role="tablist" aria-label={tx("Filtrer rom", "Filter rooms")}>
            {FILTERS.map((f) => {
              const I = f.icon;
              const count = roomTypes.filter(f.test).length;
              if (f.id !== "all" && count === 0) return null;
              return (
                <Chip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testid={`rooms-filter-${f.id}`} role="tab" aria-selected={filter === f.id}>
                  {I && <I className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />}{tx(f.nn, f.en)}
                  <span className={`tabular text-xs ${filter === f.id ? "text-background/70" : "text-muted-foreground"}`}>{count}</span>
                </Chip>
              );
            })}
          </div>

          {isLoading ? (
            <div className="mt-8 grid grid-cols-1 gap-x-7 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="rooms-loading">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-[22px] bg-muted" />)}
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-x-7 gap-y-10 sm:grid-cols-2 lg:mt-10 lg:grid-cols-3" data-testid="rooms-grid">
              {shown.map((rt, i) => (
                <Reveal key={rt.id} delay={Math.min(i, 2) * 0.05}><RoomCard rt={rt} testid={`room-row-${rt.id}`} eager={i < 3} /></Reveal>
              ))}
              {shown.length === 0 && (
                <p className="col-span-full rounded-[22px] border border-border bg-card p-8 text-muted-foreground" data-testid="rooms-empty">{tx("Ingen rom passar dette filteret.", "No rooms match this filter.")}</p>
              )}
            </div>
          )}
        </Container>
      </section>

      {/* Compare */}
      {roomTypes.length > 0 && (
        <section className="pt-20 lg:pt-32" data-testid="rooms-compare">
          <Container>
            <SectionTitle size="md" title={tx("Samanlikn romma", "Compare the rooms")} sub={tx("Alle prisar er per rom per natt med frukost og mva. Helg er fredag og laurdag.", "All rates are per room per night incl. breakfast and VAT. Weekend is Friday and Saturday.")} testid="rooms-compare-title" />
            <div className="mt-8 min-w-0 max-w-full overflow-hidden rounded-[22px] border border-border bg-card">
              <div className="overflow-x-auto">
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow className="bg-secondary/70 hover:bg-secondary/70">
                      <TableHead className="h-12 px-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Romtype", "Room type")}</TableHead>
                      <TableHead className="h-12 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">m²</TableHead>
                      <TableHead className="h-12 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Gjester", "Guests")}</TableHead>
                      <TableHead className="h-12 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Fjordutsikt", "Fjord view")}</TableHead>
                      <TableHead className="h-12 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Hund", "Dog")}</TableHead>
                      <TableHead className="h-12 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Utan trapper", "Step-free")}</TableHead>
                      <TableHead className="h-12 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Midtveke", "Weekday")}</TableHead>
                      <TableHead className="h-12 pr-5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tx("Helg", "Weekend")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roomTypes.map((r) => {
                      const yes = <Check className="mx-auto h-4 w-4 text-success" aria-label={tx("Ja", "Yes")} />;
                      const no = <span className="text-muted-foreground" aria-label={tx("Nei", "No")}>–</span>;
                      return (
                        <TableRow key={r.id} className="hover:bg-secondary/40" data-testid={`rooms-compare-row-${r.id}`}>
                          <TableCell className="px-5 py-4"><Link to={`/rom/${r.slug}`} className="font-semibold text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground">{tr(r.name)}</Link></TableCell>
                          <TableCell className="py-4 text-right tnum">{r.size_m2}</TableCell>
                          <TableCell className="py-4 text-right tnum">{r.base_guests === r.max_guests ? r.max_guests : `${r.base_guests}–${r.max_guests}`}</TableCell>
                          <TableCell className="py-4 text-center">{(r.badges || []).includes("sea_view") ? yes : no}</TableCell>
                          <TableCell className="py-4 text-center">{r.dogs_allowed ? yes : no}</TableCell>
                          <TableCell className="py-4 text-center">{r.step_free ? yes : no}</TableCell>
                          <TableCell className="py-4 text-right tnum font-semibold">{nok(r.prices.weekday.p1, lang)}</TableCell>
                          <TableCell className="py-4 pr-5 text-right tnum font-semibold">{nok(r.prices.weekend.p1, lang)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">{tx("Prisen gjeld for éin person. Pris for to og for ekstraseng finn du på kvar romside.", "Rate shown is for one guest. Rates for two and for extra beds are on each room page.")}</p>
            </div>
          </Container>
        </section>
      )}

      {/* In the room */}
      <section className="mt-20 bg-secondary/70 py-20 lg:mt-32 lg:py-32" data-testid="rooms-facilities">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionTitle size="lg" title={tx("På rommet", "In the room")} sub={tx("Det same grunnutstyret i alle rom – så kan du velje storleik og utsikt.", "The same basics in every room – then you choose size and view.")} />
            <ul className="mt-8 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2" data-testid="rooms-facilities-list">
              {facilityKeys.map((k) => {
                const f = FACILITY_META[k];
                const I = f.icon;
                return (
                  <li key={k} className="flex items-center gap-3.5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-card text-foreground ring-hairline"><I className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>
                    <span className="text-[0.9375rem] font-medium leading-snug text-foreground">{tx(f.nn, f.en)}</span>
                  </li>
                );
              })}
            </ul>
            <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-border pt-7 sm:grid-cols-4" data-testid="rooms-good-to-know">
              {[
                [tx("Innsjekk", "Check-in"), `${tx("frå", "from")} ${s?.check_in || "15:00"}`.replace(":", ".")],
                [tx("Utsjekk", "Check-out"), `${tx("før", "before")} ${s?.check_out || "12:00"}`.replace(":", ".")],
                [tx("Frukost", "Breakfast"), "07.30–10.00"],
                [tx("Hund", "Dog"), tx("250 kr per opphald", "NOK 250 per stay")],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs font-medium text-muted-foreground">{k}</dt>
                  <dd className="mt-1 text-[0.9375rem] font-semibold text-foreground tabular">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[24px] bg-card p-7 ring-hairline sm:p-8" data-testid="rooms-stepfree">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-accent-foreground"><Accessibility className="h-5 w-5" strokeWidth={1.75} /></span>
              <h3 className="mt-5 text-[1.375rem] leading-tight tracking-[-0.02em] text-foreground">{tx("Treng du eit rom utan trapper?", "Do you need a step-free room?")}</h3>
              <p className="mt-3 text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Standardromma og familieromma i første etasje er utan trapper, og restaurant, Skåno og terrassen ligg på same plan. Treng du eit tilrettelagt bad, ta kontakt før du bestiller.", "The standard and family rooms on the ground floor are step-free, and the restaurant, Skåno and the terrace are on the same level. If you need an adapted bathroom, get in touch before booking.")}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild variant="soft" data-testid="rooms-stepfree-call"><a href={HOTEL.tel}><Phone className="h-4 w-4" /> <span className="tabular">{HOTEL.phoneDisplay}</span></a></Button>
                <Button variant="ghost" onClick={() => ask(lang === "en" ? "Which rooms are step-free?" : "Kva rom er utan trapper?")} data-testid="rooms-stepfree-ask"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Common areas */}
      <section className="py-20 lg:py-32" data-testid="rooms-gallery-section">
        <Container>
          <SectionTitle size="lg" title={tx("Utanfor romdøra", "Beyond the room door")} sub={tx("Peisestove, restaurant, hage og basseng – alt i same hus eller rett utanfor.", "Fireplace lounge, restaurant, garden and pool – all in the same house or right outside.")} testid="rooms-gallery-title" />
          <Reveal className="mt-10"><HScroll items={common} testid="rooms-gallery" /></Reveal>
        </Container>
      </section>

      {/* Family / kids note + CTA */}
      <section className="pb-20 lg:pb-32" data-testid="rooms-cta">
        <Container>
          <div className="grid grid-cols-1 gap-10 border-t border-border pt-14 lg:grid-cols-12 lg:items-center lg:gap-16 lg:pt-16">
            <Reveal className="lg:col-span-5">
              <Img src="/images/pool-kids.jpg" alt={tx("Born ved kanten av bassenget", "Children at the edge of the pool")} className="img-zoom aspect-[4/3]" radius="rounded-[24px]" sizes={SIZES.half} />
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-7">
              <h2 className="text-[1.75rem] leading-[1.05] tracking-[-0.03em] text-foreground sm:text-[2.125rem]">{tx("Reiser de med born?", "Travelling with children?")}</h2>
              <p className="mt-3 max-w-[56ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Familieromma i første etasje har utgang til hagen. Barneseng og barnestol er gratis, born under 4 år bur gratis, og barnebassenget ligg rett utanfor.", "The family rooms on the ground floor open onto the garden. Cots and high chairs are free, children under 4 stay free, and the children's pool is right outside.")}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="rooms-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" /></Link></Button>
                <Button variant="soft" size="lg" onClick={() => ask(lang === "en" ? "Which room suits a family of four?" : "Kva rom passar for ein familie på fire?")} data-testid="rooms-ask-cta"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
